import {
    captureWebGlCanvasPng,
    isWebGlCanvasCaptureAvailable,
} from '../game/webglCanvasCapture'
import type { PowerUpType } from '../stores/runStateTypes'

/**
 * Card-name lookup mirrors the visual variant in `LevelCompleteCard`
 * (`cardVariantByPowerUp`).
 */
const cardFilenameByPowerUp: Record<PowerUpType, string> = {
    plane: 'StarChaser',
    heart: 'StarChaser',
    shield: 'RushGuard',
    battery: 'PowerCharger',
    coin: 'TreasureHunter',
}

const defaultCardFilename = 'StarChaser'

// Supersample factor for the share/download PNG (live canvas unchanged)
const SHARE_CARD_EXPORT_SCALE = 2

export type CardTypeRunState = {
    cardEpicEnabled: boolean
    topScoringPowerUp: PowerUpType | null
}

export function resolveCardImageFilename(runState: CardTypeRunState): string {
    if (!runState.topScoringPowerUp) {
        return defaultCardFilename
    }
    return cardFilenameByPowerUp[runState.topScoringPowerUp]
}

export function buildCardImageFilename(cardName: string): string {
    return `liferush_${cardName}_card.png`
}

function triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
}

/**
 * Exports the currently visible 3D card as a PNG and opens the native share sheet
 * with the image file as the only payload.
 *
 * Intentionally shares **only** the image file — no `title`, `text`, or `url` — so the
 * OS surfaces "Save Image" / "Save to Photos" / AirDrop ahead of social apps.
 *
 * Falls back to a classic download when the Web Share API (with files) is unavailable
 * (e.g. desktop browsers). Returns `false` only when the card could not be captured.
 */
export async function shareCardImageFromCanvas(
    runState: CardTypeRunState,
): Promise<boolean> {
    if (!isWebGlCanvasCaptureAvailable()) {
        return false
    }

    const filename = buildCardImageFilename(resolveCardImageFilename(runState))
    const blob = await captureWebGlCanvasPng({
        cropToShareCard: true,
        exportScale: SHARE_CARD_EXPORT_SCALE,
    })
    if (!blob) {
        return false
    }

    const file = new File([blob], filename, { type: 'image/png' })
    const payload: ShareData = { files: [file] }

    if (
        typeof navigator !== 'undefined' &&
        typeof navigator.share === 'function' &&
        (typeof navigator.canShare !== 'function' || navigator.canShare(payload))
    ) {
        try {
            await navigator.share(payload)
            return true
        } catch (error) {
            // User dismissed the sheet: treat as complete; only fall back on real errors
            if (error instanceof DOMException && error.name === 'AbortError') {
                return true
            }
        }
    }

    triggerDownload(blob, filename)
    return true
}
